import React from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";

import "@fontsource/dm-sans"; // Defaults to weight 400
import "@fontsource/dm-sans/400.css"; // Specify weight
import "@fontsource/dm-sans/400-italic.css"; // Specify weight and style

import "./index.css";
import App from "./App";
import { ClerkProvider } from "@clerk/clerk-react";
import { datafluxRum } from "@cloudcare/browser-rum";
import appConfig from "./utils/config";
import ModalAddPayment from "@/components/modal/ModalAddPayment";
import ModalPaymentBoundCard from "@/components/modal/ModalPaymentBoundCard";
import ModalUpdatePlan from "@/components/modal/ModalUpdatePlan";
import ModalRechargeResult from "@/components/modal/ModalRechargeResult";
import { BrowserRouter } from "react-router-dom";
import { isMobile } from "./biz/tool";
import { useModalStore } from "./hooks/useModalStore";
import { CreateOrganization, OrganizationProfile } from "@clerk/clerk-react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import WaitListShareModal from "@/pages/headChatBot/components/WaitListShareModal";
import PaymethodModal from "@/components/PaymethodModal";
import ChargeModal from "@/components/ChargeModal";
import ChangeBudgetModal from "@/components/ChangeBudgetModal";
import MatchInfluencerAdjustModal from "@/components/MatchInfluencerAdjustModal";
import AffiliateNoRechargeModal from "./components/modal/AffiliateNoRechargeModal";
import CheckDeposit from "@/components/modal/CheckDepositModal";
import PublishedVideoModal from "./components/PublishedVideoModal";
import RateModal from "./components/modal/RateModal";
// 导入Clerk官方本地化配置
import { zhCN, enUS } from "@clerk/localizations";

const PUBLISHABLE_KEY = appConfig["clerkPublishableKey"];

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk publishable key to the .env.local file");
}

// 语言代码映射（向后兼容）
const LEGACY_LANGUAGE_MAP: Record<string, string> = {
  en: "en-US",
  zh: "zh-CN",
};

// 从localStorage读取当前语言设置
const getCurrentLanguage = (): string => {
  try {
    const stored = localStorage.getItem("i18n_language");
    if (stored) {
      // 检查是否是新格式
      if (stored === "en-US" || stored === "zh-CN") {
        return stored;
      }
      // 检查是否是旧格式，进行转换
      if (stored in LEGACY_LANGUAGE_MAP) {
        const newLang = LEGACY_LANGUAGE_MAP[stored];
        // 更新到新格式
        localStorage.setItem("i18n_language", newLang);
        return newLang;
      }
    }
  } catch (error) {
    console.warn("Failed to read language from localStorage:", error);
  }
  return "en-US"; // 默认英语
};

// 获取Clerk本地化配置，使用官方包
const getClerkLocalization = () => {
  const currentLang = getCurrentLanguage();

  if (currentLang === "zh-CN") {
    return zhCN as any;
  }

  // 英语配置
  return enUS as any;
};

// if (process.env.REACT_APP_ENV !== 'development') {
// 使用 guance 云面板
// 工作空间 test/prod -> 用户访问监测 -> 应用 advertiser-platform -> 分析看板
// 地址：
// https://us1-console.guance.com/rum/list
datafluxRum.init({
  applicationId: appConfig["applicationId"],
  clientToken: appConfig["clientToken"],
  env: appConfig["env"],
  allowedTracingUrls: [appConfig["BASE_URL"]],
  version: "1.0.3",
  service: "browser",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 70,
  trackUserInteractions: true,
  site: "https://rum-openway.guance.one",
});
datafluxRum.startSessionReplayRecording();
// }

ReactGA.initialize([
  {
    trackingId: "G-X2F66DVGNZ",
  },
]);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const ModalBox = () => {
  const { showCreateOrg, showManageOrg, setShowCreateOrg, setShowManageOrg } =
    useModalStore();

  return (
    <>
      <ModalAddPayment />
      <ModalPaymentBoundCard />
      <ModalUpdatePlan />
      <ModalRechargeResult />
      <WaitListShareModal />
      {/* 支付方式选择弹窗 */}
      <PaymethodModal />
      {/* 充值弹窗 */}
      <ChargeModal />
      {/* 修改 influences budget modal */}
      <ChangeBudgetModal />
      {/* 组织管理弹窗 */}
      <Modal
        open={showManageOrg}
        onCancel={() => setShowManageOrg(false)}
        footer={null}
        classNames={{
          content: "!p-0",
        }}
        width={880}
        closeIcon={<CloseOutlined className="text-gray-500 text-lg" />}
        centered
        styles={{
          mask: {
            background: "rgba(0, 0, 0, 0.5)",
          },
        }}
        className="clerk-modal"
      >
        <OrganizationProfile />
      </Modal>
      <Modal
        open={showCreateOrg}
        onCancel={() => setShowCreateOrg(false)}
        footer={null}
        classNames={{
          content: "!p-0",
        }}
        width={432}
        closeIcon={<CloseOutlined className="text-gray-500 text-lg" />}
        centered
        styles={{
          mask: {
            background: "rgba(0, 0, 0, 0.5)",
          },
        }}
        className="clerk-modal"
      >
        <CreateOrganization />
      </Modal>
      {/* 广告主调整达人匹配结果的提交弹窗 */}
      <MatchInfluencerAdjustModal />
      {/* Affiliate 不充值弹窗 */}
      <AffiliateNoRechargeModal />
      {/* 余额不够 提示充值的弹窗 */}
      <CheckDeposit />
      {/* 发布后需要做什么的介绍视频弹窗 */}
      <PublishedVideoModal />
      {/* 达人评分弹窗 */}
      <RateModal />
    </>
  );
};

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl={`${isMobile() ? "/mLogin" : "/"}`}
        localization={{
          ...getClerkLocalization(),
          organizationProfile: {
            start: {
              profileSection: {
                uploadAction__title: "Logo(optional)",
              },
            },
          },
          signUp: {
            start: {
              title: "Welcome to Aha",
              subtitle: "",
            },
          },
          unstable__errors: {
            not_allowed_access: "Please use a work email ",
          },
        }}
        appearance={{
          variables: {
            colorPrimary: "#FB6011",
          },
        }}
      >
        <ModalBox />
        <App />
      </ClerkProvider>
    </BrowserRouter>
  </React.StrictMode>
);
